/**
 * Student behaviour, as a case rather than a punishment.
 *
 * The shape of this module is the distinction it keeps. One thing happened —
 * a fight in the cafeteria — and that is the *incident*: shared facts, shared
 * place and time, shared witnesses, shared evidence. Each student in it has
 * their own *case*: their own involvement, their own finding, their own
 * response, their own reentry. Two students in one fight can end with
 * different findings, and the data model has to let them.
 *
 * Four things are deliberately kept apart, because collapsing any pair of
 * them is how a discipline system becomes unfair:
 *
 *   what was reported   — the referral's category, an allegation
 *   what was found      — the administrator's finding, after investigating
 *   what was done       — the actions assigned, by a person
 *   what remains open   — follow-up, reentry, appeal
 *
 * Nothing here decides a consequence. There is no scoring, no escalation
 * ladder, no mapping from category to punishment. The module can show policy
 * text, prior comparable actions and required procedural steps; the decision
 * belongs to an authorised human, every time. See `POLICY_GUIDANCE_ONLY`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The incident
// ─────────────────────────────────────────────────────────────────────────────

export const INCIDENT_STATUSES = [
  'submitted',
  'under_review',
  'investigation',
  'decision_pending',
  'action_assigned',
  'reentry_pending',
  'appeal_pending',
  'resolved',
  'closed',
] as const
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number]

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under review',
  investigation: 'Investigation',
  decision_pending: 'Decision pending',
  action_assigned: 'Action assigned',
  reentry_pending: 'Reentry pending',
  appeal_pending: 'Appeal pending',
  resolved: 'Resolved',
  closed: 'Closed',
}

/**
 * An incident's status is the furthest-behind of its cases, not a thing an
 * administrator sets by hand: with two students at different stages, any
 * single hand-set value would be wrong for one of them. `incidentStatusFrom`
 * derives it.
 */
export function incidentStatusFrom(caseStatuses: readonly CaseStatus[]): IncidentStatus {
  if (caseStatuses.length === 0) return 'submitted'
  const rank: CaseStatus[] = [
    'submitted',
    'under_review',
    'investigation',
    'decision_pending',
    'action_assigned',
    'reentry_pending',
    'appeal_pending',
    'resolved',
    'closed',
    'voided',
  ]
  // A voided case does not hold an incident open; if every case is void the
  // incident is closed.
  let furthestBehind: IncidentStatus | null = null
  for (const status of caseStatuses) {
    if (status === 'voided') continue
    const candidate = status as IncidentStatus
    if (furthestBehind === null || rank.indexOf(candidate) < rank.indexOf(furthestBehind)) {
      furthestBehind = candidate
    }
  }
  return furthestBehind ?? 'closed'
}

// ─────────────────────────────────────────────────────────────────────────────
// The student case
// ─────────────────────────────────────────────────────────────────────────────

export const CASE_STATUSES = [...INCIDENT_STATUSES, 'voided'] as const
export type CaseStatus = (typeof CASE_STATUSES)[number]

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  ...INCIDENT_STATUS_LABELS,
  voided: 'Entered in error',
}

/**
 * Which status may follow which. A case moves forward, or sideways into an
 * appeal, or is voided; it never silently rewinds. Reopening is a separate,
 * reasoned, audited act (see `REOPEN_FROM`), not a transition.
 */
export const CASE_TRANSITIONS: Record<CaseStatus, readonly CaseStatus[]> = {
  submitted: ['under_review', 'voided'],
  under_review: ['investigation', 'decision_pending', 'resolved', 'voided'],
  investigation: ['decision_pending', 'resolved', 'voided'],
  decision_pending: ['action_assigned', 'resolved', 'voided'],
  action_assigned: ['reentry_pending', 'appeal_pending', 'resolved', 'voided'],
  reentry_pending: ['appeal_pending', 'resolved', 'voided'],
  appeal_pending: ['action_assigned', 'reentry_pending', 'resolved', 'voided'],
  resolved: ['closed', 'appeal_pending'],
  closed: [],
  voided: [],
}

/** A closed or resolved case can be reopened, with a reason, by the authorised. */
export const REOPEN_FROM: readonly CaseStatus[] = ['resolved', 'closed']

/** The stages a case passes through, for the progress rail in the workbench. */
export const CASE_STAGES = [
  'referral',
  'review',
  'investigation',
  'findings',
  'actions',
  'reentry',
  'follow_up',
  'closed',
] as const
export type CaseStage = (typeof CASE_STAGES)[number]

export const CASE_STAGE_LABELS: Record<CaseStage, string> = {
  referral: 'Referral',
  review: 'Review',
  investigation: 'Investigation',
  findings: 'Findings',
  actions: 'Actions',
  reentry: 'Reentry',
  follow_up: 'Follow-up',
  closed: 'Closed',
}

export const INVOLVEMENT_TYPES = ['subject', 'participant', 'target', 'witness', 'bystander', 'unknown'] as const
export type InvolvementType = (typeof INVOLVEMENT_TYPES)[number]

export const INVOLVEMENT_TYPE_LABELS: Record<InvolvementType, string> = {
  subject: 'Subject of the referral',
  participant: 'Participant',
  target: 'Person harmed',
  witness: 'Witness',
  bystander: 'Bystander',
  unknown: 'Involvement not yet determined',
}

/**
 * A case is opened for everyone the incident names, including the student who
 * was harmed — they need support, contact and follow-up too. Only these
 * involvements can carry a disciplinary action; the others carry support.
 */
export const DISCIPLINABLE_INVOLVEMENTS: readonly InvolvementType[] = ['subject', 'participant', 'unknown']

export const canCarryConsequence = (involvement: InvolvementType): boolean =>
  DISCIPLINABLE_INVOLVEMENTS.includes(involvement)

// ─────────────────────────────────────────────────────────────────────────────
// Findings — what was decided, never what was alleged
// ─────────────────────────────────────────────────────────────────────────────

export const FINDING_STATUSES = [
  'pending',
  'substantiated',
  'partially_substantiated',
  'not_substantiated',
  'inconclusive',
  'reclassified',
  'resolved_informally',
] as const
export type FindingStatus = (typeof FINDING_STATUSES)[number]

/** Default wording. A district may rename any of these in settings. */
export const FINDING_STATUS_LABELS: Record<FindingStatus, string> = {
  pending: 'No finding yet',
  substantiated: 'Substantiated',
  partially_substantiated: 'Partially substantiated',
  not_substantiated: 'Not substantiated',
  inconclusive: 'Inconclusive',
  reclassified: 'Reclassified',
  resolved_informally: 'Resolved informally',
}

/** A finding that found nothing cannot carry a consequence. */
export const FINDINGS_WITHOUT_CONSEQUENCE: readonly FindingStatus[] = ['pending', 'not_substantiated']

export interface CaseFinding {
  status: FindingStatus
  /** What the administrator concluded, in their words. Required. */
  narrative: string
  /** What that conclusion rests on: statements, evidence, admission. */
  basis: string
  /** How this student was involved, as found — not as alleged. */
  involvement?: string
  /** The rule applied, if the district runs a policy module. Free text here. */
  policyReference?: string | null
  /** If reclassified, the category it became. */
  reclassifiedCategoryId?: string | null
  notes?: string
  decidedByStaffId: string
  decidedAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Severity and priority — two different things
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Severity describes the reported incident and assists triage. It carries no
 * consequence and no authority: a teacher marking something 'major' routes it
 * faster and decides nothing.
 */
export const SEVERITIES = ['minor', 'moderate', 'major', 'critical'] as const
export type Severity = (typeof SEVERITIES)[number]

export const SEVERITY_LABELS: Record<Severity, string> = {
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  critical: 'Critical',
}

/** Priority is the office's own workflow order, set by administrators. */
export const PRIORITIES = ['routine', 'priority', 'urgent'] as const
export type Priority = (typeof PRIORITIES)[number]

export const PRIORITY_LABELS: Record<Priority, string> = {
  routine: 'Routine',
  priority: 'Priority',
  urgent: 'Urgent',
}

/** Severity suggests a starting priority; an administrator may change it. */
export const PRIORITY_FROM_SEVERITY: Record<Severity, Priority> = {
  minor: 'routine',
  moderate: 'routine',
  major: 'priority',
  critical: 'urgent',
}

/**
 * The flags on a referral that mean "this is not a paperwork matter yet".
 * Their only effect is to put emergency guidance in front of the person
 * filing, and to mark the case so the office sees it first. Discipline is not
 * an emergency response system and must not pretend to be one.
 */
export const SAFETY_FLAGS = [
  'immediate_threat',
  'weapon',
  'serious_violence',
  'medical_emergency',
  'imminent_safety',
] as const
export type SafetyFlag = (typeof SAFETY_FLAGS)[number]

export const SAFETY_FLAG_LABELS: Record<SafetyFlag, string> = {
  immediate_threat: 'An immediate threat was made',
  weapon: 'A weapon was involved',
  serious_violence: 'Serious violence occurred',
  medical_emergency: 'Someone needs medical help',
  imminent_safety: 'Someone is in danger right now',
}

export const EMERGENCY_GUIDANCE =
  'If anyone is in danger right now, use your school’s emergency procedure and call for help before filling in anything here. This form records what happened; it does not summon anyone.'

// ─────────────────────────────────────────────────────────────────────────────
// Categories — configurable, and never legal definitions
// ─────────────────────────────────────────────────────────────────────────────

export interface IncidentCategory {
  key: string
  label: string
  /** What a reporter should understand by it. District wording. */
  description?: string
  /** The triage severity this category starts at. Advisory only. */
  defaultSeverity: Severity
  /** Whether guardian contact is required by policy for this category. */
  guardianContactRequired: boolean
  /** Whether the category routes to a named administrator regardless of grade. */
  routeToStaffId?: string | null
  /** The district's own reporting code, for state and board returns. */
  reportingCode?: string | null
  /** Bus conduct, so the case offers transportation links. */
  transportationRelated?: boolean
  /** Property damage, so the case offers asset and facilities links. */
  propertyRelated?: boolean
  active: boolean
  order: number
}

/**
 * Starting categories. Deliberately plain operational wording: these are not
 * legal definitions, and a district replaces the labels in settings. Nothing
 * in the code branches on a category key except where a category is marked
 * `transportationRelated` or `propertyRelated`.
 */
export const DEFAULT_INCIDENT_CATEGORIES: readonly IncidentCategory[] = [
  { key: 'classroom_disruption', label: 'Classroom disruption', defaultSeverity: 'minor', guardianContactRequired: false, active: true, order: 1 },
  { key: 'defiance', label: 'Defiance or noncompliance', defaultSeverity: 'minor', guardianContactRequired: false, active: true, order: 2 },
  { key: 'disrespect', label: 'Disrespect', defaultSeverity: 'minor', guardianContactRequired: false, active: true, order: 3 },
  { key: 'fighting', label: 'Fighting', defaultSeverity: 'major', guardianContactRequired: true, active: true, order: 4 },
  { key: 'bullying', label: 'Bullying or harassment', defaultSeverity: 'major', guardianContactRequired: true, active: true, order: 5 },
  { key: 'threat', label: 'Threat or intimidation', defaultSeverity: 'major', guardianContactRequired: true, active: true, order: 6 },
  { key: 'vandalism', label: 'Vandalism', defaultSeverity: 'moderate', guardianContactRequired: true, propertyRelated: true, active: true, order: 7 },
  { key: 'theft', label: 'Theft', defaultSeverity: 'moderate', guardianContactRequired: true, propertyRelated: true, active: true, order: 8 },
  { key: 'technology_misuse', label: 'Technology misuse', defaultSeverity: 'moderate', guardianContactRequired: false, active: true, order: 9 },
  { key: 'academic_integrity', label: 'Academic integrity', defaultSeverity: 'moderate', guardianContactRequired: true, active: true, order: 10 },
  { key: 'bus_conduct', label: 'Bus conduct', defaultSeverity: 'moderate', guardianContactRequired: true, transportationRelated: true, active: true, order: 11 },
  { key: 'attendance_related', label: 'Attendance-related', defaultSeverity: 'minor', guardianContactRequired: false, active: true, order: 12 },
  { key: 'substance_related', label: 'Substance-related', defaultSeverity: 'critical', guardianContactRequired: true, active: true, order: 13 },
  { key: 'weapons_related', label: 'Weapons-related', defaultSeverity: 'critical', guardianContactRequired: true, active: true, order: 14 },
  { key: 'sexual_misconduct', label: 'Sexual misconduct', defaultSeverity: 'critical', guardianContactRequired: true, active: true, order: 15 },
  { key: 'property_damage', label: 'Property damage', defaultSeverity: 'moderate', guardianContactRequired: true, propertyRelated: true, active: true, order: 16 },
  { key: 'other', label: 'Other', defaultSeverity: 'minor', guardianContactRequired: false, active: true, order: 17 },
]

// ─────────────────────────────────────────────────────────────────────────────
// Statements
// ─────────────────────────────────────────────────────────────────────────────

export const STATEMENT_PERSON_TYPES = ['student', 'staff', 'guardian', 'other'] as const
export type StatementPersonType = (typeof STATEMENT_PERSON_TYPES)[number]

export const STATEMENT_ROLES = ['subject', 'witness', 'reporter', 'staff', 'other'] as const
export type StatementRole = (typeof STATEMENT_ROLES)[number]

export const STATEMENT_ROLE_LABELS: Record<StatementRole, string> = {
  subject: 'Student in the referral',
  witness: 'Witness',
  reporter: 'Reporter',
  staff: 'Staff account',
  other: 'Other',
}

/** How the words got into the record. Recorded, because it matters later. */
export const STATEMENT_SOURCES = ['typed_by_person', 'entered_by_staff', 'uploaded_scan', 'read_back_and_confirmed'] as const
export type StatementSource = (typeof STATEMENT_SOURCES)[number]

export const STATEMENT_SOURCE_LABELS: Record<StatementSource, string> = {
  typed_by_person: 'Typed by the person themselves',
  entered_by_staff: 'Written down by staff',
  uploaded_scan: 'Uploaded scan of a written statement',
  read_back_and_confirmed: 'Written by staff, read back and confirmed',
}

/**
 * Who may read a given statement or piece of evidence.
 *
 * A witness's identity is the thing this protects. A reporter does not need
 * to know who else spoke, and a family does not learn another child's name
 * from a document. `case_team` is the default: the assigned administrator and
 * those with the discipline detail tier.
 */
export const VISIBILITY_LEVELS = ['case_team', 'administrators_only', 'legal_restricted'] as const
export type VisibilityLevel = (typeof VISIBILITY_LEVELS)[number]

export const VISIBILITY_LEVEL_LABELS: Record<VisibilityLevel, string> = {
  case_team: 'The case team',
  administrators_only: 'Administrators only',
  legal_restricted: 'Restricted — legal',
}

/** Statements and evidence are never visible to a family or a reporter. */
export const NEVER_SHARED_WITH_FAMILY: readonly VisibilityLevel[] = [...VISIBILITY_LEVELS]

// ─────────────────────────────────────────────────────────────────────────────
// Evidence
// ─────────────────────────────────────────────────────────────────────────────

export const EVIDENCE_KINDS = [
  'photo',
  'video_reference',
  'document',
  'screenshot',
  'staff_report',
  'damage_photo',
  'other',
] as const
export type EvidenceKind = (typeof EVIDENCE_KINDS)[number]

export const EVIDENCE_KIND_LABELS: Record<EvidenceKind, string> = {
  photo: 'Photo',
  video_reference: 'Camera footage reference',
  document: 'Document',
  screenshot: 'Screenshot',
  staff_report: 'Staff report',
  damage_photo: 'Photo of damage',
  other: 'Other',
}

/**
 * A camera reference, for the common case where the school's camera system is
 * not connected to anything. We record where and when to look and who looked;
 * we do not claim to hold the footage, because we do not.
 */
export interface CameraReference {
  camera: string
  /** When on the footage to look, as the reviewer noted it. */
  timeOnFootage: string
  reviewedByStaffId?: string | null
  reviewedAt?: string | null
  /** What the reviewer saw. The footage itself stays in the camera system. */
  observation?: string
  /** How long that system keeps footage, if known — a deadline to act by. */
  retentionNote?: string
}

export const VIDEO_NOT_STORED_NOTE =
  'Cornerstone does not hold this footage. This is a note of where and when to find it in the school’s camera system, and who has looked.'

// ─────────────────────────────────────────────────────────────────────────────
// Actions — assigned by a person, always
// ─────────────────────────────────────────────────────────────────────────────

export const ACTION_STATUSES = ['assigned', 'scheduled', 'in_progress', 'completed', 'missed', 'cancelled', 'modified'] as const
export type ActionStatus = (typeof ACTION_STATUSES)[number]

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  assigned: 'Assigned',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  missed: 'Missed',
  cancelled: 'Cancelled',
  modified: 'Modified',
}

export const OPEN_ACTION_STATUSES: readonly ActionStatus[] = ['assigned', 'scheduled', 'in_progress', 'missed', 'modified']

/** How a consequence's length is counted. School days skip closures. */
export const DURATION_UNITS = ['none', 'minutes', 'periods', 'days', 'school_days'] as const
export type DurationUnit = (typeof DURATION_UNITS)[number]

/**
 * What a kind of action does, as configuration rather than code.
 *
 * Every integration a consequence can trigger is a field here, so a district
 * can define a consequence Cornerstone has never heard of and have it behave
 * correctly. Nothing in the module branches on a specific action key.
 */
export interface ActionType {
  key: string
  label: string
  description?: string
  durationUnit: DurationUnit
  /** Removal from the classroom or building: attendance must hear about it. */
  excludesFromClass: boolean
  /** Removal from the building, specifically. Drives reentry and attendance. */
  excludesFromSchool: boolean
  /** The attendance code to send for each excluded day. Configured, not guessed. */
  attendanceCode?: string | null
  requiresGuardianNotice: boolean
  requiresReentry: boolean
  /** Needs APPROVE, not merely EDIT, and may need a second approval step. */
  requiresApproval: boolean
  /** Further approval above the assigning administrator. */
  approvalChain?: readonly ('principal' | 'district')[]
  affectsActivityEligibility: boolean
  affectsTransportation: boolean
  /** Whether an appeal is offered for this action, if the district appeals. */
  appealable: boolean
  /** The Print Center template for its notice. */
  noticeTemplate?: string | null
  /** Supportive rather than punitive: restorative work, referrals, plans. */
  supportive: boolean
  active: boolean
  order: number
}

export const DEFAULT_ACTION_TYPES: readonly ActionType[] = [
  { key: 'warning', label: 'Warning', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: false, active: true, order: 1 },
  { key: 'conference', label: 'Student conference', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: true, order: 2 },
  { key: 'parent_conference', label: 'Parent conference', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: true, order: 3 },
  { key: 'lunch_detention', label: 'Lunch detention', durationUnit: 'days', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: false, active: true, order: 4 },
  { key: 'detention', label: 'Detention', durationUnit: 'days', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, noticeTemplate: 'discipline-notice', supportive: false, active: true, order: 5 },
  { key: 'restorative_conference', label: 'Restorative conference', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: true, order: 6 },
  { key: 'behavior_contract', label: 'Behaviour contract', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: true, order: 7 },
  { key: 'loss_of_privilege', label: 'Loss of privilege', durationUnit: 'days', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: false, active: true, order: 8 },
  { key: 'iss', label: 'In-school suspension', durationUnit: 'school_days', excludesFromClass: true, excludesFromSchool: false, attendanceCode: 'ISS', requiresGuardianNotice: true, requiresReentry: true, requiresApproval: false, affectsActivityEligibility: true, affectsTransportation: false, appealable: true, noticeTemplate: 'suspension-notice', supportive: false, active: true, order: 9 },
  { key: 'oss', label: 'Out-of-school suspension', durationUnit: 'school_days', excludesFromClass: true, excludesFromSchool: true, attendanceCode: 'OSS', requiresGuardianNotice: true, requiresReentry: true, requiresApproval: true, approvalChain: ['principal'], affectsActivityEligibility: true, affectsTransportation: false, appealable: true, noticeTemplate: 'suspension-notice', supportive: false, active: true, order: 10 },
  { key: 'alternative_placement', label: 'Alternative placement', durationUnit: 'school_days', excludesFromClass: true, excludesFromSchool: true, attendanceCode: 'ALT', requiresGuardianNotice: true, requiresReentry: true, requiresApproval: true, approvalChain: ['principal', 'district'], affectsActivityEligibility: true, affectsTransportation: true, appealable: true, noticeTemplate: 'suspension-notice', supportive: false, active: true, order: 11 },
  { key: 'restitution', label: 'Restitution', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: true, affectsActivityEligibility: false, affectsTransportation: false, appealable: true, supportive: false, active: true, order: 12 },
  { key: 'activity_restriction', label: 'Activity restriction', durationUnit: 'days', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: true, affectsTransportation: false, appealable: true, supportive: false, active: true, order: 13 },
  { key: 'transportation_restriction', label: 'Transportation restriction', durationUnit: 'days', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: true, appealable: true, noticeTemplate: 'discipline-notice', supportive: false, active: true, order: 14 },
  { key: 'support_referral', label: 'Referral to a support team', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: true, order: 15 },
  { key: 'other', label: 'Other', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: false, active: true, order: 16 },

  // Retired keys from the first discipline model. Inactive, so no picker
  // offers them, but present so that rows already written against them still
  // resolve to a label instead of showing a bare key to a family.
  { key: 'parent_contact', label: 'Parent contact', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: false, order: 90 },
  { key: 'restorative_action', label: 'Restorative action', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: false, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: false, order: 91 },
  { key: 'behavior_plan', label: 'Behaviour plan', durationUnit: 'none', excludesFromClass: false, excludesFromSchool: false, requiresGuardianNotice: true, requiresReentry: false, requiresApproval: false, affectsActivityEligibility: false, affectsTransportation: false, appealable: false, supportive: true, active: false, order: 92 },
  { key: 'expulsion_recommendation', label: 'Expulsion recommendation', durationUnit: 'none', excludesFromClass: true, excludesFromSchool: true, requiresGuardianNotice: true, requiresReentry: true, requiresApproval: true, approvalChain: ['principal', 'district'], affectsActivityEligibility: true, affectsTransportation: true, appealable: true, noticeTemplate: 'suspension-notice', supportive: false, active: false, order: 93 },
]

export const actionTypeOf = (types: readonly ActionType[], key: string): ActionType | undefined =>
  types.find((type) => type.key === key)

/**
 * The module shows guidance and never a recommendation.
 *
 * A district may configure non-binding policy text per category. What it may
 * not do is have Cornerstone pick a consequence, rank one, or derive one from
 * a count of prior incidents. If that line ever needs arguing, it is here.
 */
export const POLICY_GUIDANCE_ONLY =
  'Cornerstone does not choose consequences. This is the district’s own guidance text, shown for reference; the decision, and the responsibility for it, belong to the administrator.'

/** When an action changes, both versions survive. */
export interface ActionChange {
  at: string
  byStaffId: string
  reason: string
  from: { actionTypeKey: string; startOn: string | null; endOn: string | null; duration: number | null; status: ActionStatus }
  /** Set when the change came out of an appeal. */
  appealId?: string | null
  approvedByStaffId?: string | null
}

/** A day of a detention or in-school suspension, and whether they came. */
export interface ActionAttendanceDay {
  date: string
  attended: boolean
  /** Why they were not there, if they were not. */
  note?: string
  recordedByStaffId: string
  recordedAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Guardian contact
// ─────────────────────────────────────────────────────────────────────────────

export const GUARDIAN_CONTACT_STATUSES = ['not_required', 'pending', 'attempted', 'completed', 'unable_to_reach'] as const
export type GuardianContactStatus = (typeof GUARDIAN_CONTACT_STATUSES)[number]

export const GUARDIAN_CONTACT_STATUS_LABELS: Record<GuardianContactStatus, string> = {
  not_required: 'Not required',
  pending: 'Pending',
  attempted: 'Attempted',
  completed: 'Completed',
  unable_to_reach: 'Unable to reach',
}

/**
 * An attempt is not a contact. Whether 'attempted' is enough to proceed is a
 * district policy question, so it is a setting — and the default is no.
 */
export const ATTEMPT_IS_NOT_CONTACT =
  'Someone tried and did not reach them. Whether that is enough to proceed is a district decision, set in Discipline rules.'

// ─────────────────────────────────────────────────────────────────────────────
// Reentry
// ─────────────────────────────────────────────────────────────────────────────

export const REENTRY_STATUSES = ['not_required', 'due', 'scheduled', 'completed', 'cleared', 'follow_up_due'] as const
export type ReentryStatus = (typeof REENTRY_STATUSES)[number]

export const REENTRY_STATUS_LABELS: Record<ReentryStatus, string> = {
  not_required: 'Not required',
  due: 'Meeting due',
  scheduled: 'Meeting scheduled',
  completed: 'Meeting held',
  cleared: 'Return cleared',
  follow_up_due: 'Follow-up due',
}

export interface ReentryParticipant {
  role: 'administrator' | 'student' | 'guardian' | 'counselor' | 'teacher' | 'other'
  name: string
  staffId?: string | null
  attended: boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Appeals
// ─────────────────────────────────────────────────────────────────────────────

export const APPEAL_STATUSES = [
  'none',
  'submitted',
  'under_review',
  'hearing_scheduled',
  'decision_pending',
  'upheld',
  'modified',
  'reversed',
  'withdrawn',
] as const
export type AppealStatus = (typeof APPEAL_STATUSES)[number]

export const APPEAL_STATUS_LABELS: Record<AppealStatus, string> = {
  none: 'No appeal',
  submitted: 'Submitted',
  under_review: 'Under review',
  hearing_scheduled: 'Hearing scheduled',
  decision_pending: 'Decision pending',
  upheld: 'Upheld',
  modified: 'Modified',
  reversed: 'Reversed',
  withdrawn: 'Withdrawn',
}

export const APPEAL_OPEN_STATUSES: readonly AppealStatus[] = ['submitted', 'under_review', 'hearing_scheduled', 'decision_pending']

export const APPEAL_DECIDED_STATUSES: readonly AppealStatus[] = ['upheld', 'modified', 'reversed', 'withdrawn']

/** Whether a consequence runs while an appeal is pending is district policy. */
export const ACTION_DURING_APPEAL = ['continues', 'pauses'] as const
export type ActionDuringAppeal = (typeof ACTION_DURING_APPEAL)[number]

// ─────────────────────────────────────────────────────────────────────────────
// Notes
// ─────────────────────────────────────────────────────────────────────────────

export const NOTE_LEVELS = ['case', 'administrators_only', 'legal_restricted'] as const
export type NoteLevel = (typeof NOTE_LEVELS)[number]

export const NOTE_LEVEL_LABELS: Record<NoteLevel, string> = {
  case: 'Case note',
  administrators_only: 'Administrators only',
  legal_restricted: 'Restricted — legal',
}

// ─────────────────────────────────────────────────────────────────────────────
// Routing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How a referral finds its administrator. Rules are data: a school sets them
 * up once and no person's name appears in the code. First match wins, in
 * `order`; a rule with no conditions is the catch-all.
 */
export interface RoutingRule {
  id: string
  schoolId?: string | null
  /** Inclusive grade range, as grade-level strings in the school's own terms. */
  gradeLevels?: readonly string[]
  categoryKeys?: readonly string[]
  severities?: readonly Severity[]
  /** The administrator who gets it. */
  staffId: string
  order: number
  active: boolean
}

export function routeReferral(
  rules: readonly RoutingRule[],
  referral: { schoolId: string; gradeLevel?: string | null; categoryKey: string; severity: Severity },
): RoutingRule | null {
  const ordered = [...rules].filter((rule) => rule.active).sort((a, b) => a.order - b.order)
  for (const rule of ordered) {
    if (rule.schoolId && rule.schoolId !== referral.schoolId) continue
    if (rule.gradeLevels?.length && !rule.gradeLevels.includes(referral.gradeLevel ?? '')) continue
    if (rule.categoryKeys?.length && !rule.categoryKeys.includes(referral.categoryKey)) continue
    if (rule.severities?.length && !rule.severities.includes(referral.severity)) continue
    return rule
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Deadlines
// ─────────────────────────────────────────────────────────────────────────────

export const DEADLINE_KINDS = [
  'referral_review',
  'guardian_contact',
  'investigation',
  'finding',
  'reentry_meeting',
  'reentry_follow_up',
  'appeal_decision',
] as const
export type DeadlineKind = (typeof DEADLINE_KINDS)[number]

export const DEADLINE_KIND_LABELS: Record<DeadlineKind, string> = {
  referral_review: 'Review the referral',
  guardian_contact: 'Contact the guardian',
  investigation: 'Finish the investigation',
  finding: 'Enter a finding',
  reentry_meeting: 'Hold the reentry meeting',
  reentry_follow_up: 'Follow up after the return',
  appeal_decision: 'Decide the appeal',
}

/** Hours, because a referral review is measured in hours, not days. */
export interface DeadlineRule {
  kind: DeadlineKind
  hours: number
  /** Measured against the end of the school day rather than the clock. */
  byEndOfSchoolDay?: boolean
  active: boolean
}

export const DEFAULT_DEADLINE_RULES: readonly DeadlineRule[] = [
  { kind: 'referral_review', hours: 4, active: true },
  { kind: 'guardian_contact', hours: 8, byEndOfSchoolDay: true, active: true },
  { kind: 'investigation', hours: 48, active: true },
  { kind: 'finding', hours: 72, active: true },
  { kind: 'reentry_meeting', hours: 0, active: true },
  { kind: 'reentry_follow_up', hours: 168, active: true },
  { kind: 'appeal_decision', hours: 240, active: true },
]

export type DeadlineState = 'ahead' | 'due_soon' | 'due_today' | 'overdue'

/**
 * How late a thing is, in words the queue can sort by. No colour, no score —
 * the caller decides how to show it.
 */
export function deadlineState(dueAt: string, now: Date = new Date()): DeadlineState {
  const due = new Date(dueAt).getTime()
  const current = now.getTime()
  if (current > due) return 'overdue'
  const hoursLeft = (due - current) / 3_600_000
  if (hoursLeft <= 4) return 'due_today'
  if (hoursLeft <= 24) return 'due_soon'
  return 'ahead'
}

/** "2h overdue", "due in 3h", "due today" — for the queue, in plain words. */
export function deadlineWording(dueAt: string, now: Date = new Date()): string {
  const due = new Date(dueAt).getTime()
  const diffHours = (due - now.getTime()) / 3_600_000
  if (diffHours < 0) {
    const late = Math.abs(diffHours)
    if (late < 1) return 'overdue'
    if (late < 24) return `${Math.floor(late)}h overdue`
    return `${Math.floor(late / 24)}d overdue`
  }
  if (diffHours < 1) return 'due within the hour'
  if (diffHours < 8) return `due in ${Math.floor(diffHours)}h`
  if (diffHours < 24) return 'due today'
  return `due in ${Math.floor(diffHours / 24)}d`
}

// ─────────────────────────────────────────────────────────────────────────────
// School days
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Counts forward `days` school days from `startOn`, skipping anything the
 * calendar says is not a school day.
 *
 * A two-day suspension starting on a Friday ends on Monday. Getting this
 * wrong by counting calendar days means a student is marked excluded on days
 * the school was shut, and returns on a day nobody expects them.
 */
export function schoolDaysForward(startOn: string, days: number, isSchoolDay: (date: string) => boolean): string[] {
  const out: string[] = []
  if (days <= 0) return out
  const cursor = new Date(`${startOn}T12:00:00.000Z`)
  let guard = 0
  while (out.length < days && guard < 400) {
    const date = cursor.toISOString().slice(0, 10)
    if (isSchoolDay(date)) out.push(date)
    cursor.setUTCDate(cursor.getUTCDate() + 1)
    guard += 1
  }
  return out
}

/** The first school day after an exclusion ends: the day they come back. */
export function returnDateAfter(lastExcludedDate: string, isSchoolDay: (date: string) => boolean): string | null {
  const cursor = new Date(`${lastExcludedDate}T12:00:00.000Z`)
  for (let guard = 0; guard < 400; guard += 1) {
    cursor.setUTCDate(cursor.getUTCDate() + 1)
    const date = cursor.toISOString().slice(0, 10)
    if (isSchoolDay(date)) return date
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Patterns — factual, and never a score
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What the record actually says, over a stated window, in the student's own
 * categories. Counted, never weighted.
 *
 * There is deliberately no risk score, no behaviour score and no prediction
 * anywhere in this module. A count with its window stated is a fact an
 * administrator can check and a family can contest; a score is neither. If a
 * future requirement asks for one, it needs a policy decision first, not a
 * function here.
 */
export interface IncidentPattern {
  windowDays: number
  /** Incidents in the window, whatever their finding. */
  total: number
  /** Only those where something was found. The honest denominator. */
  substantiated: number
  /** Same category as the case in hand, which is what "related" means here. */
  relatedSameCategory: number
  byCategory: readonly { categoryKey: string; label: string; count: number }[]
  /** One sentence, already worded, so no caller invents a label. */
  sentence: string
}

export const NO_BEHAVIOR_SCORE =
  'Cornerstone does not score students. These are counted incidents over a stated window, with their findings; they are not a risk rating and must not be used as one.'

export function patternSentence(total: number, windowDays: number, substantiated: number): string {
  if (total === 0) return `No other incidents in the last ${windowDays} days.`
  const incidents = total === 1 ? '1 incident' : `${total} incidents`
  if (substantiated === 0) return `${incidents} in the last ${windowDays} days, none substantiated.`
  if (substantiated === total) return `${incidents} in the last ${windowDays} days, all substantiated.`
  return `${incidents} in the last ${windowDays} days, ${substantiated} substantiated.`
}

// ─────────────────────────────────────────────────────────────────────────────
// Protected-support safeguards
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What an administrator sees when the student has an IEP, a 504 or another
 * protected support, and a removal is being considered.
 *
 * This is the whole of it: that a procedural review may be required, and a
 * route to the team who would run it. No diagnosis, no evaluation data, no
 * plan contents. Whether a review is actually required is a legal
 * determination the special programs team makes, not something derived here
 * from a day count.
 */
export const SPECIAL_PROGRAMS_PROCEDURAL_ALERT =
  'This student has a protected support plan. A Special Programs review may be required before a removal of this kind. Send the procedural review request and wait for their answer — Cornerstone cannot make that determination.'

export const SPECIAL_PROGRAMS_NO_DETAIL_NOTE =
  'Discipline shows only that a plan exists and who to ask. Plan contents, evaluations and disability information stay in Special Programs.'

// ─────────────────────────────────────────────────────────────────────────────
// Overrides
// ─────────────────────────────────────────────────────────────────────────────

/** Stepping outside the configured workflow. Permission, reason, audit. */
export const OVERRIDE_KINDS = [
  'close_without_guardian_contact',
  'close_without_reentry',
  'skip_investigation',
  'modify_completed_action',
  'reopen_case',
  'void_case',
] as const
export type OverrideKind = (typeof OVERRIDE_KINDS)[number]

export const OVERRIDE_KIND_LABELS: Record<OverrideKind, string> = {
  close_without_guardian_contact: 'Close without reaching a guardian',
  close_without_reentry: 'Close without the reentry meeting',
  skip_investigation: 'Decide without an investigation',
  modify_completed_action: 'Change an action already completed',
  reopen_case: 'Reopen a closed case',
  void_case: 'Mark the case entered in error',
}

export interface OverrideRecord {
  kind: OverrideKind
  reason: string
  byStaffId: string
  at: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Numbering
// ─────────────────────────────────────────────────────────────────────────────

/** `DISC-2026-001284`. The year is the school year the incident fell in. */
export const incidentNumber = (year: number, sequence: number): string =>
  `DISC-${year}-${String(sequence).padStart(6, '0')}`

/** `DISC-2026-001284-A`, `-B`, … one per student, in the order they joined. */
export const caseNumber = (incident: string, index: number): string =>
  `${incident}-${String.fromCharCode(65 + (index % 26))}${index >= 26 ? String(Math.floor(index / 26)) : ''}`

// ─────────────────────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────────────────────

/** Everything a district configures about discipline, in one document. */
export interface DisciplineRules {
  categories: readonly IncidentCategory[]
  actionTypes: readonly ActionType[]
  routingRules: readonly RoutingRule[]
  deadlines: readonly DeadlineRule[]
  findingLabels?: Partial<Record<FindingStatus, string>>
  /** Whether the district runs an appeal process at all. */
  appealsEnabled: boolean
  /** Who may appeal, in the district's words. Shown, not enforced. */
  appealWindowDays?: number
  actionDuringAppeal: ActionDuringAppeal
  /** Whether an attempted contact satisfies the guardian-contact requirement. */
  attemptSatisfiesContact: boolean
  /** Whether a reentry meeting is needed after any exclusion, not just OSS. */
  reentryAfterAnyExclusion: boolean
  /** Days back the prior-history panel looks. Stated wherever counts appear. */
  historyWindowDays: number
  /** Non-binding guidance text per category key. Never a recommendation. */
  policyGuidance?: Readonly<Record<string, string>>
  /** The district's code of conduct reference, shown on notices. */
  codeOfConductReference?: string | null
}

export const DEFAULT_DISCIPLINE_RULES: DisciplineRules = {
  categories: DEFAULT_INCIDENT_CATEGORIES,
  actionTypes: DEFAULT_ACTION_TYPES,
  routingRules: [],
  deadlines: DEFAULT_DEADLINE_RULES,
  appealsEnabled: true,
  appealWindowDays: 10,
  actionDuringAppeal: 'continues',
  attemptSatisfiesContact: false,
  reentryAfterAnyExclusion: true,
  historyWindowDays: 90,
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The first discipline model: one row per student, with the investigation and
 * the reentry folded into it. Kept because rows written against it still
 * exist and still have to open.
 *
 * `scripts/migrate-discipline-cases.mjs` gives each of those rows a shared
 * incident to belong to; these types describe what it reads.
 */
export interface DisciplineInvestigation {
  statements?: string
  witnesses?: string[]
  evidence?: string
  findings: string
  investigatedByStaffId: string
  investigatedAt: string
}

export interface DisciplineReentry {
  meetingOn: string
  guardianPresent: boolean
  conditions?: string
  followUpOn?: string | null
  recordedByStaffId: string
  recordedAt: string
}

/** The statuses the first model used, and what each became. */
export const LEGACY_STATUS_MAP: Record<string, CaseStatus> = {
  open: 'submitted',
  under_review: 'under_review',
  action_assigned: 'action_assigned',
  reentry_pending: 'reentry_pending',
  resolved: 'resolved',
}

export const DISCIPLINE_STATUSES = ['open', 'under_review', 'action_assigned', 'reentry_pending', 'resolved'] as const
export type DisciplineStatus = (typeof DISCIPLINE_STATUSES)[number]

export const DISCIPLINE_TRANSITIONS: Record<DisciplineStatus, readonly DisciplineStatus[]> = {
  open: ['under_review', 'resolved'],
  under_review: ['action_assigned', 'resolved'],
  action_assigned: ['reentry_pending', 'resolved'],
  reentry_pending: ['resolved'],
  resolved: [],
}

export interface DisciplineActionType {
  key: string
  label: string
  requiresReentry: boolean
  requiresApproval: boolean
  requiresDates: boolean
}

/** The old typed list, derived from the configurable one so they cannot drift. */
export const DISCIPLINE_ACTION_TYPES: readonly DisciplineActionType[] = DEFAULT_ACTION_TYPES.filter(
  (type) => type.key !== 'other',
).map((type) => ({
  key: type.key,
  label: type.label,
  requiresReentry: type.requiresReentry,
  requiresApproval: type.requiresApproval,
  requiresDates: type.durationUnit !== 'none',
}))

export const disciplineActionType = (key: string): DisciplineActionType | undefined =>
  DISCIPLINE_ACTION_TYPES.find((action) => action.key === key)
